import { startTransition, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import type { z } from "zod";

import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type { CustomerDetails } from "~/shared/schemas/customer-schema.ts";
import {
	creditLimitSchema,
	customerDetailsSchema,
} from "~/shared/schemas/customer-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import { scrollMainContentToTop } from "~/shared/utils/scroll-to-top.ts";

export const getStepFromRoute = (pathname: string): number => {
	if (pathname.includes("/carrier-selection/")) {
		return 2;
	}
	if (pathname.includes("/shipment-details/")) {
		return 3;
	}
	if (pathname.includes("/quote-details")) {
		return 1;
	}
	return 0; // Default to quote actions
};

// Custom hook for managing step navigation
export const useStepNavigation = () => {
	const location = useLocation();
	const [currentStep, setCurrentStep] = useState(() =>
		getStepFromRoute(location.pathname),
	);

	useEffect(() => {
		const newStep = getStepFromRoute(location.pathname);

		if (newStep !== currentStep) {
			startTransition(() => {
				setCurrentStep(newStep);
			});
		}
	}, [location.pathname, currentStep]);

	return { currentStep, setCurrentStep };
};

// Custom hook for managing selected carrier
export const useSelectedCarrier = (quoteState: QuoteStateType) => {
	const [selectedCarrier, setSelectedCarrier] = useState<CarrierPrice | null>(
		() => {
			if (!quoteState.selectedCarrierPriceId) return null;
			return (
				quoteState.carrierPrices.find(
					(cp: CarrierPrice) => cp.id === quoteState.selectedCarrierPriceId,
				) ?? null
			);
		},
	);

	useEffect(() => {
		if (!quoteState.selectedCarrierPriceId) {
			setSelectedCarrier(null);
			return;
		}

		const carrier = quoteState.carrierPrices.find(
			(cp: CarrierPrice) => cp.id === quoteState.selectedCarrierPriceId,
		);
		setSelectedCarrier(carrier ?? null);
	}, [quoteState.selectedCarrierPriceId, quoteState.carrierPrices]);

	return selectedCarrier;
};

// Custom hook for customer data fetching
export const useCustomerDataFetch = (
	customerId: number | undefined,
	setCustomerDetails: (data: CustomerDetails) => void,
	setCreditDetails: (data: {
		customerCredits: z.infer<typeof creditLimitSchema>;
	}) => void,
	isInternal: boolean,
) => {
	const latestCustomerIdRef = useRef<number | undefined>(customerId);

	useEffect(() => {
		latestCustomerIdRef.current = customerId;
	}, [customerId]);

	useEffect(() => {
		if (!customerId || customerId <= 0 || !isInternal) return;

		const requestedCustomerId = customerId;

		const timeoutId = setTimeout(() => {
			void (async () => {
				try {
					const [fullCustomerData, customerCredit] = await Promise.all([
						getAndValidate(
							`${apiRoutes.SELECTCUSTOMER}${customerId}`,
							customerDetailsSchema,
						),
						getAndValidate(
							`${apiRoutes.CREDITINFO}${customerId}`,
							creditLimitSchema,
						),
					]);

					if (latestCustomerIdRef.current === requestedCustomerId) {
						if (fullCustomerData?.success) {
							setCustomerDetails(fullCustomerData.data);
						} else {
							console.error(
								"Failed to fetch customer details:",
								fullCustomerData?.error,
							);
						}
					}

					if (latestCustomerIdRef.current === requestedCustomerId) {
						if (customerCredit?.success) {
							setCreditDetails({ customerCredits: customerCredit.data });
						} else {
							console.error(
								"Failed to fetch customer credit details:",
								customerCredit?.error,
							);
						}
					}
				} catch (error) {
					console.error("Error fetching customer data:", error);
				}
			})();
		}, 100); // Debounce by 100ms

		return () => clearTimeout(timeoutId);
	}, [customerId, setCreditDetails, setCustomerDetails, isInternal]);
};

// Custom hook for scroll management
export const useScrollToTop = (currentStep: number) => {
	useEffect(() => {
		startTransition(() => {
			setTimeout(() => {
				scrollMainContentToTop();
			}, 0);
		});
	}, [currentStep]);
};
